import { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { listAdminWorkspaces, type AdminWorkspaceRow, type Page } from "../../../features/admin/api/adminApi";
import { PageHeader, StatCard, StatusPill, money } from "./_shared";
import WorkspaceBillingEditor from "./WorkspaceBillingEditor";

/** Per-workspace billing: assign manual plans, switch billing mode, see MRR. */
export default function AdminBilling() {
  const [data, setData] = useState<Page<AdminWorkspaceRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    listAdminWorkspaces({ page, search, limit: 50 }).then(setData).catch(() => undefined).finally(() => setLoading(false));
  };
  useEffect(load, [page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = useMemo(() => {
    const rows = data?.rows ?? [];
    const paying = rows.filter((r) => r.plan && r.plan.monthlyPrice > 0);
    return {
      mrr: paying.reduce((s, r) => s + (r.plan?.monthlyPrice ?? 0), 0),
      manual: rows.filter((r) => r.billingType === "manual").length,
      stripe: rows.filter((r) => r.billingType === "stripe").length,
    };
  }, [data]);

  return (
    <>
      <PageHeader title="Billing" subtitle="Assign plans and manage billing mode per workspace" />
      <div className="p-4 sm:p-6">
        <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="MRR (this page)" value={money(summary.mrr)} tone="gold" />
          <StatCard label="Manual billing" value={summary.manual} />
          <StatCard label="Stripe billing" value={summary.stripe} />
          <StatCard label="Workspaces" value={data?.total ?? 0} />
        </div>

        <div className="relative mb-4 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search workspace…"
            className="w-full rounded-lg border border-ink-200 py-2 pl-9 pr-3 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
          />
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center text-ink-400"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            {(data?.rows ?? []).map((ws) => (
              <div key={ws.id} className="surface-card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-ink-900">{ws.name}</p>
                    <p className="text-[11.5px] text-ink-400">{ws.owner ? ws.owner.email : "no owner"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={ws.billingType} />
                    <span className="text-[12px] text-ink-600">
                      {ws.plan ? `${ws.plan.name} · ${money(ws.plan.monthlyPrice)}/mo` : "No plan"}
                      {ws.planLapsed && <span className="ml-1 text-rose">(lapsed)</span>}
                    </span>
                  </div>
                </div>
                <div className="mt-3">
                  <WorkspaceBillingEditor
                    workspaceId={ws.id}
                    billingType={ws.billingType}
                    planId={ws.plan?.id ?? null}
                    manualPlanEndsAt={ws.manualPlanEndsAt}
                    onSaved={load}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2 text-[12.5px]">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-ink-200 px-3 py-1.5 disabled:opacity-40">Prev</button>
            <span className="text-ink-500">Page {page} of {data.totalPages}</span>
            <button disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-ink-200 px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </>
  );
}
